import { cn } from '@/lib/utils'
import { ToggleButton } from '@/components/ui/toggle-button'
import type { AriaAttributes, ComponentType, ReactNode } from 'react'

export type FilterPillOption<TValue extends string = string> = {
  value: TValue
  label: ReactNode
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: AriaAttributes['aria-hidden'] }>
}

export type FilterPillGroupProps<TValue extends string = string> = {
  ariaLabel?: string
  value?: TValue
  options: FilterPillOption<TValue>[]
  onValueChange?: (value: TValue) => void
  className?: string
}

export function FilterPillGroup<TValue extends string = string>({
  ariaLabel,
  value,
  options,
  onValueChange,
  className,
}: FilterPillGroupProps<TValue>) {
  return (
    <div role="group" aria-label={ariaLabel} className={cn('inline-block max-w-full', className)}>
      <div className="inline-flex max-w-full flex-wrap items-center justify-center gap-1 rounded-xl border border-border bg-card p-1.5">
        {options.map(({ value: optionValue, label, icon: Icon }) => {
          const isActive = value === optionValue

          return (
            <ToggleButton
              key={optionValue}
              type="button"
              size="xs"
              variant="ghost"
              pressedVariant="selected"
              pressed={isActive}
              className={cn(
                'h-8 gap-2 px-3',
                !isActive && 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => onValueChange?.(optionValue)}
            >
              {Icon ? <Icon className="size-4" aria-hidden="true" /> : null}
              {label}
            </ToggleButton>
          )
        })}
      </div>
    </div>
  )
}
